import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAuthToken } from '../../auth/context/AuthContext';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/admin`;

function createdDay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

function createdLabel(iso) {
  const day = createdDay(iso);
  if (!day) return '—';
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export default function JobCosts() {
  const [jobs, setJobs] = useState(null);
  const [err, setErr] = useState('');
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const res = await fetch(`${API}/wallet`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not load job costs');
        if (!gone) setJobs(data.jobs || []);
      } catch (e) {
        if (!gone) setErr(e.message);
      }
    })();
    return () => { gone = true; };
  }, []);

  const rangeInvalid = Boolean(startDate && endDate && startDate > endDate);
  const filtered = useMemo(() => {
    if (!jobs || rangeInvalid) return [];
    const needle = title.trim().toLowerCase();
    return jobs.filter(job => {
      if (needle && !(job.title || '').toLowerCase().includes(needle)) return false;
      const day = createdDay(job.created_at);
      if ((startDate || endDate) && !day) return false;
      if (startDate && day < startDate) return false;
      if (endDate && day > endDate) return false;
      return true;
    });
  }, [jobs, title, startDate, endDate, rangeInvalid]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Cost by job</div>
          <div className="page-subtitle">
            What this company has been charged for each job. Interview charges include Deepgram speech.
          </div>
        </div>
      </div>
      {err && <div className="alert alert-error">{err}</div>}
      {jobs === null && !err && <div className="empty-state-text">Loading…</div>}
      {jobs && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <label>
              <div className="form-label">Job title</div>
              <input
                className="form-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Search title"
              />
            </label>
            <label>
              <div className="form-label">Created from</div>
              <input
                className="form-input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
            <label>
              <div className="form-label">Created to</div>
              <input
                className="form-input"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
          </div>
          {rangeInvalid && (
            <div style={{ fontSize: 12, color: 'var(--danger, #b91c1c)', marginTop: 8 }}>
              Created from is after Created to.
            </div>
          )}
        </div>
      )}
      {jobs && jobs.length === 0 && (
        <div className="card" style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>
          No job charges yet.
        </div>
      )}
      {jobs && jobs.length > 0 && !rangeInvalid && filtered.length === 0 && (
        <div className="card" style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>
          No jobs match these filters.
        </div>
      )}
      {filtered.length > 0 && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                <th>Job</th>
                <th>Created</th>
                <th>Deducted</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(job => (
                <tr key={job.job_id}>
                  <td>
                    <Link to={`/jobs/${job.job_id}`} style={{ fontWeight: 600 }}>
                      {job.title}
                    </Link>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{createdLabel(job.created_at)}</td>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{job.deducted_display}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
